import axios from 'axios';
import type {
  AuthenticatedUser,
  ImportResult,
  NavicStatus,
  NavicVolunteer,
  NavicVolunteerDetail,
  NavicWorkgroup,
  StatusCounts,
  WorkgroupRequest,
  WorkgroupRequestPatch,
} from '../types/api';

// In production the backend serves this bundle, so the API is same-origin.
// The localhost fallback only applies to `vite dev` against a local backend.
// Anything that links to a backend URL (e.g. the OAuth kick-off) must use
// this — never its own VITE_API_URL fallback.
export const backendBaseUrl =
  import.meta.env.VITE_API_URL ??
  (import.meta.env.DEV ? 'http://localhost:5050' : '');
const baseURL = backendBaseUrl;

export const api = axios.create({
  baseURL,
  withCredentials: true,
});

export async function getMe(): Promise<AuthenticatedUser> {
  const { data } = await api.get<AuthenticatedUser>('/api/me');
  return data;
}

export async function listRequests(): Promise<WorkgroupRequest[]> {
  const { data } = await api.get<WorkgroupRequest[]>('/api/workgroup-requests');
  return data;
}

export async function getStatusCounts(): Promise<StatusCounts> {
  const { data } = await api.get<StatusCounts>(
    '/api/workgroup-requests/status-counts',
  );
  return data;
}

export async function patchRequest(
  id: string,
  patch: WorkgroupRequestPatch,
): Promise<WorkgroupRequest> {
  const { data } = await api.patch<WorkgroupRequest>(
    `/api/workgroup-requests/${id}`,
    patch,
  );
  return data;
}

export async function softDeleteRequest(id: string): Promise<WorkgroupRequest> {
  const { data } = await api.post<WorkgroupRequest>(
    `/api/workgroup-requests/${id}/soft-delete`,
  );
  return data;
}

export async function submitToHrp(id: string): Promise<WorkgroupRequest> {
  const { data } = await api.post<WorkgroupRequest>(
    `/api/workgroup-requests/${id}/submit-to-hrp`,
  );
  return data;
}

export async function importFromSheet(): Promise<ImportResult> {
  const { data } = await api.post<ImportResult>('/api/workgroup-requests/import');
  return data;
}

// ---- NAVIC integration ----

export async function getNavicStatus(): Promise<NavicStatus> {
  const { data } = await api.get<NavicStatus>('/api/navic/status');
  return data;
}

export async function searchNavicVolunteers(q: string): Promise<NavicVolunteer[]> {
  const { data } = await api.get<{ volunteers: NavicVolunteer[] }>(
    '/api/navic/volunteers',
    { params: q ? { q } : {} },
  );
  return data.volunteers;
}

export async function getNavicVolunteer(id: string): Promise<NavicVolunteerDetail> {
  const { data } = await api.get<{ volunteer: NavicVolunteerDetail }>(
    `/api/navic/volunteers/${id}`,
  );
  return data.volunteer;
}

export async function promoteToNavic(
  id: string,
): Promise<{ request: WorkgroupRequest; navicWorkgroup: NavicWorkgroup }> {
  const { data } = await api.post<{
    request: WorkgroupRequest;
    navicWorkgroup: NavicWorkgroup;
  }>(`/api/workgroup-requests/${id}/promote-to-navic`);
  return data;
}
